import { api } from "@/lib/api";
import { isHubIosShell } from "@/lib/hubShell";
import { openHrefInNewTab } from "@/lib/openExternalLink";

/**
 * A company's own ATW Hub app inside its Slack workspace (V26.2.183). Slack's history limits for
 * apps outside the Marketplace don't apply to a workspace's internal apps, so previews and unread
 * counts work there. The admin adds its keys once; anyone connecting that workspace uses it.
 */
export type SlackCompanyApp = {
  id: string;
  clientId: string;
  /** "pending" until the first sign-in proves the keys and names the workspace. */
  status: "pending" | "active";
  teamId: string;
  teamName: string;
  teamDomain: string;
  createdAt: string;
  verifiedAt: string | null;
  /** Hub users connected through this app. */
  connected: number;
};

/** What the admin pastes into api.slack.com → Create New App → From a manifest (YAML). */
export const SLACK_COMPANY_APP_MANIFEST = `display_information:
  name: ATW Hub
  description: Your Slack conversations in ATW Hub (hub.atw.net).
  background_color: "#103448"
oauth_config:
  redirect_urls:
    - https://api.hub.atw.net/oauth/slack/callback
  scopes:
    user:
      - channels:history
      - channels:read
      - chat:write
      - groups:history
      - groups:read
      - im:history
      - im:read
      - im:write
      - mpim:history
      - mpim:read
      - mpim:write
      - search:read
      - users:read
      - reactions:read
      - reactions:write
      - files:read
settings:
  org_deploy_enabled: false
  socket_mode_enabled: false
  token_rotation_enabled: false
`;

/** "https://ImportGenius.slack.com/" or "importgenius.slack.com" → "importgenius". */
export function slackWorkspaceName(input: string): string {
  return input
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/\/.*$/, "")
    .replace(/\.(enterprise\.)?slack\.com$/, "");
}

/** Link an admin sends to staff: opens Connections with the workspace filled in. */
export function slackWorkspaceConnectLink(domain: string): string {
  return `${window.location.origin}/settings/connections/chat?slackWorkspace=${encodeURIComponent(domain)}`;
}

export async function fetchSlackCompanyApps(): Promise<SlackCompanyApp[]> {
  const body = await api<{ apps?: SlackCompanyApp[] }>("/api/chat/slack-apps");
  return Array.isArray(body.apps) ? body.apps : [];
}

/** Saves the keys and returns the Slack sign-in that checks them. */
export async function addSlackCompanyApp(
  clientId: string,
  clientSecret: string,
): Promise<{ app: SlackCompanyApp; authorizationUrl: string }> {
  return api("/api/chat/slack-apps", {
    method: "POST",
    body: JSON.stringify({ clientId: clientId.trim(), clientSecret: clientSecret.trim() }),
  });
}

export async function removeSlackCompanyApp(id: string): Promise<void> {
  await api(`/api/chat/slack-apps/${encodeURIComponent(id)}`, { method: "DELETE" });
}

/** Connect through a workspace's company app; the API answers 404 when it has none. */
export async function startSlackWorkspaceConnect(workspace: string): Promise<{ authorizationUrl?: string; message?: string }> {
  return api("/api/integrations/slack/connect", {
    method: "POST",
    body: JSON.stringify({ addAccount: true, workspace: slackWorkspaceName(workspace) }),
  });
}

/** Open Slack in a new tab before the request finishes, so popup blockers allow it. */
export async function openSlackSignIn(load: () => Promise<string | undefined>): Promise<void> {
  if (isHubIosShell()) {
    const href = await load();
    if (!href) throw new Error("Slack didn't return a sign-in link.");
    openHrefInNewTab(href);
    return;
  }
  const tab = window.open("about:blank", "_blank");
  try {
    const href = await load();
    if (!href) throw new Error("Slack didn't return a sign-in link.");
    if (tab) {
      tab.opener = null;
      tab.location.href = href;
    } else {
      openHrefInNewTab(href);
    }
  } catch (err) {
    tab?.close();
    throw err;
  }
}
